/**
 * InheritedBindingsPanel — read-only ancestor-chain bindings + "Override
 * here" action (PIM Wave P1 Task 8, design notes Surface 4). Implements
 * USER RULING C3 (`p1-task0-recon.md` C3 / `p1-task8-brief.md`): there is no
 * resolved-bindings-with-provenance endpoint, so this panel is assembled
 * CLIENT-SIDE — the ancestor chain comes from the drained flat category list
 * (`BindingEditorPage`'s `ancestorIdsOf`, nearest-first), each ancestor's
 * DIRECT bindings are fetched via `useAncestorCategoryBindings` (parallel
 * `useQueries`, same key/fetcher shape as `useCategoryBindings`), and
 * "nearest wins" is resolved here with `computeAncestorGroups`.
 *
 * Shadowing algorithm (brief §5): walk the ancestor chain nearest → farthest,
 * seeding the "claimed" field-id set with the CURRENT category's direct
 * bindings (a direct binding always shadows every ancestor). For each
 * ancestor's bindings (in that nearest-first order): a binding is shadowed
 * iff its `field_id` is already claimed (by the current category or a
 * nearer ancestor already visited); otherwise it is the currently-EFFECTIVE
 * source for that field, and it claims the id so any farther ancestor with
 * the same field renders shadowed too. This makes "ancestor bindings of
 * fields also bound directly ALWAYS shown-but-shadowed" fall out for free
 * (the direct seed), and correctly shadows a farther ancestor's binding when
 * a NEARER ancestor (not the current category) already binds the same
 * field — not just the direct-vs-ancestor case.
 *
 * "Override here" creates a direct binding on the current category, copying
 * `requirement`/`visibility_rule`/`matching_weight_override` from THAT
 * ancestor row verbatim (brief §5) — disabled only when the field is
 * ALREADY bound directly on the current category (clicking would 409
 * `catalog_schema.binding.duplicate`); still enabled for a row shadowed by a
 * NEARER ancestor (not yet direct) — overriding from a farther row is still
 * a legal, meaningful action.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";

import { Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import type { AdminCategory } from "@/features/admin/categories/api";
import type { CategoryBinding } from "@/features/admin/category-bindings/api";
import {
  useAncestorCategoryBindings,
  useCreateCategoryBinding,
} from "@/features/admin/category-bindings/hooks";

interface AncestorBindingRow {
  binding: CategoryBinding;
  shadowed: boolean;
}

interface AncestorGroup {
  ancestorId: string;
  rows: AncestorBindingRow[];
}

/**
 * Nearest-wins shadow resolution — see file header. Exported for
 * readability/testability even though nothing outside this module imports
 * it (TESTING IS DESCOPED — no test file added).
 */
export function computeAncestorGroups(
  ancestorIds: string[],
  ancestorBindingsById: Map<string, CategoryBinding[]>,
  directFieldIds: ReadonlySet<string>,
): AncestorGroup[] {
  const claimed = new Set<string>(directFieldIds);
  const groups: AncestorGroup[] = [];
  for (const ancestorId of ancestorIds) {
    const bindings = ancestorBindingsById.get(ancestorId) ?? [];
    const rows: AncestorBindingRow[] = bindings.map((binding) => {
      const shadowed = claimed.has(binding.field_id);
      if (!shadowed) claimed.add(binding.field_id);
      return { binding, shadowed };
    });
    groups.push({ ancestorId, rows });
  }
  return groups;
}

function categoryLabel(node: AdminCategory, lang: string): string {
  if (lang === "ky" && node.name_ky) return node.name_ky;
  if (lang === "en" && node.name_en) return node.name_en;
  return node.name_ru;
}

export interface InheritedBindingsPanelProps {
  categoryId: string;
  /** Nearest-first (immediate parent first, root last) — see
   * `BindingEditorPage`'s `ancestorIdsOf`. */
  ancestorIds: string[];
  ancestors: AdminCategory[];
  fieldsById: Map<string, AttributeField>;
  directFieldIds: ReadonlySet<string>;
  /** Append position for an "Override here" create — the current direct
   * bindings count (appends at the end of the direct table). */
  nextSortOrder: number;
  lang: string;
}

export function InheritedBindingsPanel({
  categoryId,
  ancestorIds,
  ancestors,
  fieldsById,
  directFieldIds,
  nextSortOrder,
  lang,
}: InheritedBindingsPanelProps): ReactElement | null {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const ancestorsById = useMemo(
    () => new Map(ancestors.map((c) => [c.id, c])),
    [ancestors],
  );

  const ancestorQueries = useAncestorCategoryBindings(ancestorIds);
  const ancestorBindingsById = useMemo(() => {
    const map = new Map<string, CategoryBinding[]>();
    ancestorIds.forEach((ancestorId, index) => {
      const bindings = ancestorQueries[index]?.data?.bindings ?? [];
      map.set(
        ancestorId,
        bindings.slice().sort((a, b) => a.sort_order - b.sort_order),
      );
    });
    return map;
  }, [ancestorIds, ancestorQueries]);

  const groups = useMemo(
    () => computeAncestorGroups(ancestorIds, ancestorBindingsById, directFieldIds),
    [ancestorIds, ancestorBindingsById, directFieldIds],
  );
  const totalRows = groups.reduce((n, g) => n + g.rows.length, 0);

  const [expanded, setExpanded] = useState(false);
  const [pendingFieldId, setPendingFieldId] = useState<string | null>(null);
  const createMutation = useCreateCategoryBinding();

  const isLoading = ancestorQueries.some((q) => q.isLoading);
  const isError = ancestorQueries.some((q) => q.isError);

  const handleOverride = (binding: CategoryBinding): void => {
    setPendingFieldId(binding.field_id);
    createMutation.mutate(
      {
        categoryId,
        body: {
          field_id: binding.field_id,
          requirement: binding.requirement,
          sort_order: nextSortOrder,
          visibility_rule: binding.visibility_rule,
          matching_weight_override: binding.matching_weight_override,
        },
      },
      {
        onSuccess: () => toast.success(t("pim.bindings.inherited.overrideSuccess")),
        onError: (err) => {
          if (err instanceof ApiError && err.code === "catalog_schema.binding.duplicate") {
            toast.error(t("pim.bindings.picker.duplicate"));
          } else {
            toast.error(t("error.generic"));
          }
        },
        onSettled: () => setPendingFieldId(null),
      },
    );
  };

  // Nothing to show at all — no ancestors in the chain (a root category).
  if (ancestorIds.length === 0) return null;

  return (
    <section
      data-testid="pim-bindings-inherited-panel"
      className="flex flex-col gap-2 rounded-[var(--g-radius-md)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3"
    >
      <button
        type="button"
        data-testid="pim-bindings-inherited-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[44px] items-center gap-1.5 self-start text-sm font-medium text-[var(--g-primary-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
      >
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={expanded ? "rotate-180 transition-transform" : "transition-transform"}
        />
        {t("pim.bindings.inherited.title", { count: totalRows })}
      </button>

      {expanded ? (
        isLoading ? (
          <p className="text-sm text-[var(--g-secondary)]">{t("pim.bindings.inherited.loading")}</p>
        ) : isError ? (
          <p role="alert" className="text-sm text-[var(--g-error)]">
            {t("pim.bindings.inherited.loadError")}
          </p>
        ) : totalRows === 0 ? (
          <p className="text-sm text-[var(--g-secondary)]">{t("pim.bindings.inherited.empty")}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {groups
              .filter((g) => g.rows.length > 0)
              .map((group) => {
                const ancestor = ancestorsById.get(group.ancestorId);
                const ancestorName = ancestor ? categoryLabel(ancestor, lang) : group.ancestorId;
                return (
                  <div key={group.ancestorId} className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium uppercase tracking-wide text-[var(--g-secondary)]">
                      {t("pim.bindings.inherited.sourceLabel", { name: ancestorName })}
                    </span>
                    <ul className="flex flex-col gap-2" data-testid={`pim-bindings-inherited-group-${group.ancestorId}`}>
                      {group.rows.map(({ binding, shadowed }) => {
                        const field = fieldsById.get(binding.field_id);
                        const fieldName = field?.name_ru ?? binding.field_id;
                        const typeMeta = field ? fieldTypeMeta(field.field_type) : undefined;
                        const alreadyDirect = directFieldIds.has(binding.field_id);
                        const isPending = pendingFieldId === binding.field_id && createMutation.isPending;
                        return (
                          <li
                            key={binding.id}
                            data-testid="pim-binding-inherited-row"
                            className={[
                              "flex flex-wrap items-center gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-2.5",
                              shadowed ? "opacity-60" : "",
                            ].join(" ")}
                          >
                            <div className="flex min-w-[10rem] flex-1 flex-col gap-0.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="truncate text-sm font-medium">{fieldName}</span>
                                {typeMeta ? (
                                  <GPill tone="neutral" size="sm">
                                    {t(typeMeta.labelKey)}
                                  </GPill>
                                ) : null}
                                {shadowed ? (
                                  <GPill tone="neutral" size="sm">
                                    {t("pim.bindings.inherited.overriddenChip")}
                                  </GPill>
                                ) : null}
                              </div>
                              <span className="truncate font-mono text-xs text-[var(--g-secondary)]">
                                {field?.slug ?? binding.field_id}
                              </span>
                            </div>

                            <span
                              className={[
                                "text-sm",
                                shadowed ? "line-through text-[var(--g-secondary)]" : "",
                              ].join(" ")}
                            >
                              {t(`pim.bindings.requirement.${binding.requirement}`)}
                            </span>

                            {binding.matching_weight_override !== null ? (
                              <span className="text-xs text-[var(--g-secondary)]">
                                {t("pim.bindings.inherited.weightOverride", {
                                  value: binding.matching_weight_override,
                                })}
                              </span>
                            ) : null}

                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              loading={isPending}
                              disabled={alreadyDirect}
                              onClick={() => handleOverride(binding)}
                              data-testid={`pim-binding-override-${binding.id}`}
                              className="min-h-[44px]"
                            >
                              {t(
                                alreadyDirect
                                  ? "pim.bindings.inherited.alreadyBound"
                                  : "pim.bindings.inherited.overrideHere",
                              )}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
          </div>
        )
      ) : null}
    </section>
  );
}

export default InheritedBindingsPanel;
